import React, { useEffect, useState, useRef } from "react";
import bgTestFallback from "../assets/bg_test.jpg";
import coupleAvatar from "../assets/couple.jpg";
import ceoAvatar from "../assets/ceo.jpeg";
import birthdayAvatar from "../assets/birthday.jpeg";

const fallback = {
  title: "What Our Clients Say",
  subtitle: "Real stories from Parivar Wedding Event celebrations & events",
  bg_image: null,
  items: [
    {
      text: "Parivar Wedding event made our wedding truly magical! Everything was perfect and stress-free.",
      name: "Aarav & Sneha",
      role: "Wedding Couple",
      avatar: coupleAvatar,
      accent: "#ffd452",
    },
    {
      text: "Excellent event management — our corporate retreat was flawless!",
      name: "Mr. Sharma",
      role: "CEO, Kalyan Group",
      avatar: ceoAvatar,
      accent: "#b0354c",
    },
    {
      text: "From decor to entertainment, Parivar Wedding Event nailed it all. Highly recommended.",
      name: "Niharika Jain",
      role: "Birthday Host",
      avatar: birthdayAvatar,
      accent: "#6b1331",
    },
    {
      text: "The team went above and beyond for our anniversary celebration. Truly unforgettable!",
      name: "Priya & Rahul",
      role: "Anniversary Celebration",
      avatar: coupleAvatar,
      accent: "#ffd452",
    },
    {
      text: "Professional, creative, and incredibly organized. Best event planners in town!",
      name: "Ananya Desai",
      role: "Corporate Event",
      avatar: ceoAvatar,
      accent: "#b0354c",
    },
    {
      text: "They transformed our venue into a dreamland. Every detail was perfect!",
      name: "Vikram & Neha",
      role: "Wedding Couple",
      avatar: birthdayAvatar,
      accent: "#6b1331",
    },
  ],
};

function Testimonials() {
  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/content/testimonials.json", { cache: "no-store" });
        if (!res.ok) throw new Error("No CMS file");
        const json = await res.json();
        if (!cancelled && json) {
          setData({
            title: json.title || fallback.title,
            subtitle: json.subtitle || fallback.subtitle,
            bg_image: json.bg_image || null,
            items: Array.isArray(json.items) && json.items.length ? json.items : fallback.items,
          });
        }
      } catch {
        if (!cancelled) setData(fallback);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const bgImg = data.bg_image || bgTestFallback;
  const items = data.items || [];
  
  // Double the items for seamless infinite scroll
  const duplicatedItems = [...items, ...items];

  return (
    <section
      id="testimonials"
      aria-label="Client testimonials"
      style={{
        background: `
          linear-gradient(135deg, rgba(255, 248, 252, 0.95) 0%, rgba(255, 240, 245, 0.98) 100%),
          url(${bgImg}) center/cover no-repeat fixed
        `,
        padding: "40px 0 48px 0",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div 
        className="container" 
        style={{ 
          maxWidth: 1200, 
          margin: "0 auto", 
          padding: "0 20px",
          position: "relative", 
          zIndex: 2 
        }}
      >
        <header style={{ textAlign: "center", marginBottom: 28 }}>
          <h2
            style={{
              fontFamily: "'Playfair Display', 'Georgia', serif",
              fontWeight: 700,
              color: "#6b1331",
              fontSize: "clamp(1.5rem, 3vw, 2rem)",
              textAlign: "center",
              letterSpacing: "-0.02em",
              marginBottom: 6,
              lineHeight: 1.2,
            }}
          >
            {data.title}
          </h2>
          <div
            style={{
              color: "#8a4565",
              fontSize: "0.9rem",
              fontWeight: 400,
              maxWidth: 400,
              margin: "0 auto",
              lineHeight: 1.4,
            }}
          >
            {data.subtitle}
          </div>
          <div
            style={{
              width: 40,
              height: 3,
              background: "linear-gradient(90deg, #ffd452, #b0354c)",
              margin: "12px auto 0",
              borderRadius: 2,
            }}
          />
        </header>

        {loading && (
          <div 
            role="status" 
            aria-live="polite"
            style={{ 
              textAlign: "center", 
              color: "#8a4565", 
              fontSize: 14,
              padding: "20px 0",
            }}
          >
            Loading testimonials…
          </div>
        )}

        {/* Marquee Container */}
        <div
          style={{
            position: "relative",
            width: "100%",
            overflow: "hidden",
            maskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
            WebkitMaskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
          }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setIsPaused(false)}
        >
          {/* Scrolling Track */}
          <div
            ref={trackRef}
            style={{
              display: "flex",
              gap: 20,
              width: "max-content",
              animation: `scroll 40s linear infinite`,
              animationPlayState: isPaused ? "paused" : "running",
            }}
          >
            {duplicatedItems.map((t, i) => {
              const accent = t.accent || ["#ffd452", "#b0354c", "#6b1331"][i % 3];
              
              return (
                <article
                  key={i}
                  style={{
                    background: "#ffffff",
                    borderRadius: 16,
                    boxShadow: "0 2px 16px rgba(107, 19, 49, 0.06)",
                    padding: "20px",
                    minWidth: "280px",
                    maxWidth: "320px",
                    flexShrink: 0,
                    borderTop: `3px solid ${accent}`,
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    cursor: "grab",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.02)";
                    e.currentTarget.style.boxShadow = "0 8px 28px rgba(107, 19, 49, 0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 2px 16px rgba(107, 19, 49, 0.06)";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 12, gap: 12 }}>
                    {t.avatar ? (
                      <img
                        src={t.avatar}
                        alt={`${t.name} profile`}
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: `2px solid ${accent}`,
                          flexShrink: 0,
                        }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        loading="lazy"
                      />
                    ) : (
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          background: `linear-gradient(135deg, ${accent}30, ${accent}10)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          color: accent,
                          fontSize: 16,
                          border: `2px solid ${accent}`,
                          flexShrink: 0,
                        }}
                      >
                        {t.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                      </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <h3 
                        style={{ 
                          color: "#6b1331", 
                          fontWeight: 700, 
                          fontSize: "0.95rem",
                          margin: 0,
                          lineHeight: 1.3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {t.name}
                      </h3>
                      <p 
                        style={{ 
                          color: "#9b6b7d", 
                          fontSize: "0.8rem", 
                          fontWeight: 500,
                          margin: "2px 0 0 0",
                          lineHeight: 1.3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {t.role}
                      </p>
                    </div>
                  </div>

                  <blockquote
                    style={{
                      margin: 0,
                      padding: 0,
                      border: "none",
                      color: "#5c3a45",
                      fontSize: "0.9rem",
                      lineHeight: 1.5,
                      fontStyle: "normal",
                      flex: 1,
                    }}
                  >
                    <span style={{ color: accent, fontWeight: 700 }}>“</span>
                    {t.text}
                    <span style={{ color: accent, fontWeight: 700 }}>”</span>
                  </blockquote>
                </article>
              );
            })}
          </div>
        </div>

        {/* Trust indicator */}
        <div
          style={{
            marginTop: 24,
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "#8a4565",
              fontSize: "0.85rem",
              fontWeight: 500,
              padding: "6px 14px",
              background: "rgba(255,255,255,0.7)",
              borderRadius: 50,
            }}
          >
            <span style={{ color: "#ffd452", fontSize: "0.85rem" }}>★★★★★</span>
            <span>Trusted by 500+ happy couples</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        /* Pause animation on hover for better UX */
        #testimonials:hover .scrolling-track {
          animation-play-state: paused;
        }
        
        @media (max-width: 768px) {
          #testimonials {
            padding: 32px 0 40px 0 !important;
          }
          #testimonials header {
            margin-bottom: 20px !important;
          }
          #testimonials article {
            min-width: 260px !important;
            max-width: 280px !important;
            padding: 16px !important;
          }
        }
        
        @media (max-width: 480px) {
          #testimonials article {
            min-width: 240px !important;
            max-width: 260px !important;
          }
          #testimonials .container {
            padding: 0 12px !important;
          }
        }
        
        /* Smooth touch scrolling for mobile */
        @media (hover: none) and (pointer: coarse) {
          #testimonials > div > div[style*="overflow"] {
            overflow-x: auto !important;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          #testimonials > div > div[style*="overflow"]::-webkit-scrollbar {
            display: none;
          }
          #testimonials article {
            scroll-snap-align: start;
          }
        }
      `}</style>
    </section>
  );
}

export default Testimonials;